import { handleCatchError } from '@common/utils'
import { MolsonLeaveGuard } from '@components/reusable'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useBlocker, useNavigate } from 'react-router-dom'

type UsePreventNavigationProps = {
  shouldPreventNavigation: boolean
  onSave: () => Promise<void>
  onDiscard: () => Promise<void> | void
}

export const usePreventNavigationV2 = ({
  shouldPreventNavigation,
  onDiscard,
  onSave
}: UsePreventNavigationProps) => {
  const { t } = useTranslation('')
  const navigate = useNavigate()
  const [showDialog, setShowDialog] = useState(false)
  const pendingLocationRef = useRef<any>(null)
  const bypassBlockingRef = useRef(false)

  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      shouldPreventNavigation &&
      !bypassBlockingRef.current &&
      !nextLocation.state?.saveAction &&
      currentLocation.pathname !== nextLocation.pathname
  )

  const handleNavigationAfterAction = useCallback(
    async (action: () => Promise<void> | void) => {
      try {
        const pendingLocation = pendingLocationRef.current
        await action()

        setShowDialog(false)
        bypassBlockingRef.current = true

        navigate(
          pendingLocation.pathname +
            (pendingLocation.search ?? '') +
            (pendingLocation.hash ?? '')
        )

        setTimeout(() => {
          bypassBlockingRef.current = false
          pendingLocationRef.current = null
        }, 100)
      } catch (error) {
        const errorMessage = `Error during navigation action: ${error}`
        handleCatchError(errorMessage)
        setShowDialog(false)
      }
    },
    [navigate]
  )

  const handleContinue = useCallback(async () => {
    await handleNavigationAfterAction(onSave)
  }, [handleNavigationAfterAction, onSave])

  const handleDiscard = useCallback(async () => {
    await handleNavigationAfterAction(onDiscard)
  }, [handleNavigationAfterAction, onDiscard])

  const handleClose = useCallback(() => {
    setShowDialog(false)
    pendingLocationRef.current = null
  }, [])

  useEffect(() => {
    if (blocker.state === 'blocked' && blocker.location?.state?.saveAction) {
      blocker.proceed()
      return
    }

    if (blocker.state === 'blocked') {
      pendingLocationRef.current = blocker.location
      setShowDialog(true)
    } else if (
      blocker.state === 'proceeding' ||
      blocker.state === 'unblocked'
    ) {
      setShowDialog(false)
    }
  }, [blocker])

  const LeaveGuard = showDialog ? (
    <MolsonLeaveGuard
      handleConfirm={handleContinue}
      cancelBtnText={t('common.leaveWithoutSaving') ?? ''}
      closeLeaveConfirmationDialog={handleDiscard}
      confirmBtnText={t('common.saveChanges') ?? ''}
      isLeaveConfirmationOpen={showDialog}
      onClose={handleClose}
      subtitle={t('common.confirmToProceed') ?? ''}
      title={t('common.leaveWithoutSavingChanges') ?? ''}
    />
  ) : null

  return {
    LeaveGuard,
    isPending: showDialog,
    proceed: handleContinue,
    cancel: handleDiscard
  }
}
